import * as Sentry from '@sentry/react';

import { PlatformBridge } from './bridge';

export interface DevicePosition {
  lat: number;
  lon: number;
  /** GPS accuracy in meters, when available */
  accuracy?: number;
}

interface CapacitorGeolocationPlugin {
  requestPermissions(): Promise<{ location: string }>;
  getCurrentPosition(options?: {
    enableHighAccuracy?: boolean;
    timeout?: number;
  }): Promise<{ coords: { latitude: number; longitude: number; accuracy: number } }>;
}

const PERMISSION_GRANTED = 'granted';

function getCapacitorGeolocation(): CapacitorGeolocationPlugin | null {
  const plugins = PlatformBridge.capacitorPlugins;
  if (!plugins) return null;
  return (plugins.Geolocation as CapacitorGeolocationPlugin) ?? null;
}

function toPosition(
  coords: { latitude: number; longitude: number; accuracy?: number },
): DevicePosition | null {
  const { latitude: lat, longitude: lon, accuracy } = coords;
  if (typeof lat !== 'number' || typeof lon !== 'number') return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return {
    lat,
    lon,
    ...(typeof accuracy === 'number' && Number.isFinite(accuracy)
      ? { accuracy }
      : {}),
  };
}

function getWebPosition(): Promise<DevicePosition | null> {
  return new Promise(resolve => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      success => resolve(toPosition(success.coords)),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  });
}

export const LocationBridge = {
  /**
   * Device GPS position (Capacitor plugin on native, navigator.geolocation on
   * web). Returns null when unavailable — never throws, callers just omit it.
   */
  async getPosition(): Promise<DevicePosition | null> {
    try {
      const geolocation = getCapacitorGeolocation();
      if (geolocation) {
        const permission = await geolocation.requestPermissions().catch(() => null);
        if (permission && permission.location !== PERMISSION_GRANTED) {
          return null;
        }
        const position = await geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10_000,
        });
        return toPosition(position.coords);
      }
      return await getWebPosition();
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  },
};
